"use client";

import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import {
  TypePackageDetailItem,
  TypePackageModificationItemSkeleton,
} from "@contentful/types";
import { faStar } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { sendGTMEvent } from "@next/third-parties/google";
import { GtmEvents, getYzsaId } from "@yardzen/next-client-util";
import { B } from "@yardzen/ui/components/b";
import { Badge } from "@yardzen/ui/components/badge";
import { Button } from "@yardzen/ui/components/button";
import { Checkbox } from "@yardzen/ui/components/checkbox";
import { Eyebrow } from "@yardzen/ui/components/eyebrow";
import { H1 } from "@yardzen/ui/components/h1";
import { H2 } from "@yardzen/ui/components/h2";
import { Icon, isIconName } from "@yardzen/ui/components/icon";
import { Label } from "@yardzen/ui/components/label";
import { Link } from "@yardzen/ui/components/link";
import { P } from "@yardzen/ui/components/p";
import {
  RadioCardGroupWithTags,
  RadioCardWithTagsOption,
} from "@yardzen/ui/components/radio-card-group-with-tags";
import { Select, SelectItem } from "@yardzen/ui/components/select";
import { cn } from "@yardzen/ui/lib/utils";
import { cva } from "class-variance-authority";
import { Entry } from "contentful";
import Cookies from "js-cookie";
import { useSearchParams } from "next/navigation";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useBuilderPartnerCheckoutHref } from "./builderPartnerCheckoutHref";
import { ListItemCollection } from "./ListItemCollection";
import { hasPromoPrice, showsPromoTotal } from "./pricing";
import { REVIEWS_PAGE_HREF, YARDZEN_REVIEW_COUNT } from "./reviewSummary";

export function PackageDetailItem({
  packageDetailItem,
  variant,
  lotSize,
  splitTreatment,
  packageImageUrl,
  initialModificationOption,
  currentModificationOption,
  setCurrentModificationOption,
  onAddonPriceChange,
  isMinimalDetail,
  showPriceWhileMinimal,
  addToCartId = "pdp-add-to-cart",
}: {
  packageDetailItem: TypePackageDetailItem<"WITHOUT_UNRESOLVABLE_LINKS">;
  variant?: "detail" | "preview" | "modal" | undefined;
  lotSize?: string;
  splitTreatment?: boolean;
  packageImageUrl?: string;
  initialModificationOption?:
    | Entry<
        TypePackageModificationItemSkeleton,
        "WITHOUT_UNRESOLVABLE_LINKS",
        string
      >
    | undefined;
  currentModificationOption?:
    | Entry<
        TypePackageModificationItemSkeleton,
        "WITHOUT_UNRESOLVABLE_LINKS",
        string
      >
    | undefined;
  setCurrentModificationOption?: (
    option: Entry<
      TypePackageModificationItemSkeleton,
      "WITHOUT_UNRESOLVABLE_LINKS",
      string
    >,
  ) => void;
  onAddonPriceChange?: (totalPrice: number) => void;
  isMinimalDetail?: boolean;
  showPriceWhileMinimal?: boolean;
  /** DOM id for the Add to Cart wrapper — must be unique per rendered detail
   *  item so the floating card's IntersectionObserver tracks the right one. */
  addToCartId?: string;
}) {
  const {
    primaryEyebrow,
    listItems,
    heading,
    fullPackageName,
    price,
    promoPrice,
    numberOfInstallments,
    pricePerInstallment,
    description,
    icon1,
    icon2,
    modificationOptions,
    addOnItems,
    cta,
    packagesPageCta,
    uniqueIdentifier,
  } = packageDetailItem?.fields || {};

  const [currentAddonItems, setCurrentAddonItems] = useState<
    {
      stripeProductId: string;
      price: number;
    }[]
  >([]);

  // Track if VIEW_ITEM event has been sent to prevent duplicates
  const viewItemEventSent = useRef(false);

  // Calculate total price of selected addon items
  const totalAddonPrice = currentAddonItems.reduce(
    (total, item) => total + item.price,
    0,
  );

  // Notify parent component when addon price changes
  useEffect(() => {
    onAddonPriceChange?.(totalAddonPrice);
  }, [totalAddonPrice, onAddonPriceChange]);

  const [localModificationOption, setLocalModificationOption] = useState<
    | Entry<
        TypePackageModificationItemSkeleton,
        "WITHOUT_UNRESOLVABLE_LINKS",
        string
      >
    | undefined
  >(initialModificationOption ?? undefined);

  const [hostName, setHostName] = useState<string>("");

  useEffect(() => {
    setHostName(window.location.origin);
  }, []);

  const searchParams = useSearchParams();
  const isRecommended = searchParams?.get("is-recommended") === "true";
  const withBuilderPartnerParams = useBuilderPartnerCheckoutHref();

  const getGTMEventData = useCallback(
    (
      price: number,
      promoPrice: number | undefined,
      item_variant_id: string | undefined,
      item_name: string | undefined,
    ) => {
      return {
        ecommerce: {
          currency: "USD",
          value: price,
          transaction_id: "",
          items: [
            {
              item_variant_id: item_variant_id ?? "",
              item_id: packageDetailItem?.fields.name,
              item_name: item_name ?? "",
              price: promoPrice ?? price,
              discount: promoPrice != null ? price - promoPrice : 0,
              image_url: packageImageUrl,
              item_category_id: 536,
            },
          ],
        },
      };
    },
    [packageDetailItem?.fields.name, packageImageUrl],
  );

  useEffect(() => {
    if (
      !price ||
      !fullPackageName ||
      !uniqueIdentifier ||
      viewItemEventSent.current
    ) {
      return;
    }
    sendGTMEvent({
      event: GtmEvents.VIEW_ITEM,
      ...getGTMEventData(price, promoPrice, uniqueIdentifier, fullPackageName),
    });

    // Mark that the event has been sent
    viewItemEventSent.current = true;
  }, [fullPackageName, uniqueIdentifier, price, promoPrice, getGTMEventData]);

  // Wrapper function to handle modification option changes
  const handleModificationOptionChange = useCallback(
    (
      option: Entry<
        TypePackageModificationItemSkeleton,
        "WITHOUT_UNRESOLVABLE_LINKS",
        string
      >,
    ) => {
      if (setCurrentModificationOption) {
        setCurrentModificationOption(option);
      } else {
        setLocalModificationOption(option);
      }
    },
    [setCurrentModificationOption],
  );

  // Use either prop or local state
  const effectiveModificationOption =
    currentModificationOption ?? localModificationOption;

  const chooseModificationOption = (e: ChangeEvent<HTMLSelectElement>) => {
    const chosenOption = modificationOptions?.find(
      (option) => option?.fields.internalValue === e?.target?.value,
    );
    // if for some reason the select value wasn't valid and the chosen option wasn't found, just ignore
    if (chosenOption) {
      handleModificationOptionChange(chosenOption);
    }
  };

  // Radio Button version
  const chooseModificationOptionValue = (value: string) => {
    const chosenOption = modificationOptions?.find(
      (option) => option?.fields.internalValue === value,
    );
    // if for some reason the select value wasn't valid and the chosen option wasn't found, just ignore
    if (chosenOption) {
      handleModificationOptionChange(chosenOption);
    }
  };
  const chooseAddonItem = (stripeProductId: string, price: number) => {
    setCurrentAddonItems((prev) =>
      prev.some((item) => item.stripeProductId === stripeProductId)
        ? prev.filter((item) => item.stripeProductId !== stripeProductId)
        : [...prev, { stripeProductId, price }],
    );
  };

  const variantResolved = variant ?? "preview";

  const displayDescription = variantResolved === "preview";

  // if this Package Detail Item has modification options, we need to utilize that for the price,
  // installments, installment price, CTA button, etc.
  const ctaToUse = effectiveModificationOption
    ? effectiveModificationOption?.fields.cta
    : variantResolved === "detail"
      ? cta
      : packagesPageCta;
  // Add yzsa parameter to url like wordpress was doing.

  const challengerUrl = ctaToUse?.fields.challengerURL ?? "";
  const controlUrl = ctaToUse?.fields.url ?? "";

  // If split treatment is on and challenger URL is defined, use the challenger URL.
  const fallbackUrl = new URL("https://yardzen.com");
  let ctaUrl: URL = fallbackUrl;

  try {
    const urlToUse =
      splitTreatment && challengerUrl ? challengerUrl : controlUrl;
    if (urlToUse && urlToUse.trim() !== "") {
      const baseUrl = hostName || "https://yardzen.com";
      ctaUrl = urlToUse.startsWith("http")
        ? new URL(urlToUse)
        : new URL(urlToUse, baseUrl);
    }
  } catch (error) {
    console.warn("Invalid URL provided for CTA:", {
      challengerUrl,
      controlUrl,
      hostName,
    });
    ctaUrl = fallbackUrl;
  }
  const yzsaCookie = getYzsaId();

  if (
    variantResolved === "detail" &&
    yzsaCookie &&
    ctaUrl &&
    !ctaUrl.searchParams.has("yzsa")
  ) {
    ctaUrl.searchParams.set("yzsa", encodeURIComponent(yzsaCookie));
  }

  // Checkout page requires extra packageDetail param
  if (
    variantResolved === "detail" &&
    uniqueIdentifier &&
    ctaUrl &&
    !ctaUrl.searchParams.has("packageDetail")
  ) {
    ctaUrl.searchParams.set(
      "packageDetail",
      encodeURIComponent(uniqueIdentifier),
    );
  }
  if (currentAddonItems && ctaUrl) {
    currentAddonItems.forEach((addonItem) => {
      ctaUrl.searchParams.append(
        "addonItems",
        encodeURIComponent(addonItem.stripeProductId),
      );
    });
  }

  const headingToUse =
    variantResolved === "detail" && fullPackageName ? fullPackageName : heading;

  const priceToUse = effectiveModificationOption
    ? effectiveModificationOption.fields.price
    : price;
  const promoPriceToUse = effectiveModificationOption
    ? effectiveModificationOption.fields.promoPrice
    : promoPrice;
  const installmentsToUse = effectiveModificationOption
    ? effectiveModificationOption.fields.numberOfInstallments
    : numberOfInstallments;
  const installmentPriceToUse = effectiveModificationOption
    ? effectiveModificationOption.fields.pricePerInstallment
    : pricePerInstallment;

  const pricePresentationVariant =
    variantResolved === "modal"
      ? "Modal"
      : variantResolved === "detail"
        ? "Below Header"
        : (packageDetailItem?.fields.pricePresentation ?? "Next to Header");
  const pricePresentationVariants = cva("", {
    variants: {
      headerContainer: {
        "Above CTA": "",
        "Next to Header":
          "flex flex-col lg:flex-row justify-between md:items-center",
        "Below Header": "flex flex-col",
        Modal: "flex flex-col",
      },
      priceContainer: {
        "Above CTA":
          "text-brand-primary-darker font-display mt-2 font-normal mb-2 md:mb-0 pt-2 md:pt-8 text-left text-base md:text-lg flex flex-1 items-end",
        "Next to Header":
          "text-brand-primary-darker font-display mt-2 font-normal text-left text-lg md:pl-2",
        "Below Header":
          "text-brand-primary-darker font-display text-left text-3xl md:text-4xl font-normal flex flex-row flex-wrap items-center",
        Modal:
          "text-brand-primary-darker font-display text-left text-2xl font-normal flex flex-row flex-wrap items-center",
      },
    },
  });

  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  const layoutVariants = cva("", {
    variants: {
      headingText: {
        detail: "font-display text-left text-4xl lg:text-5xl font-normal",
        preview: "font-display text-left text-3xl font-normal",
        modal: "font-display text-left text-3xl font-normal",
      },
    },
  });

  const affirmLogoIconName = "affirm-logo";

  const packageOptions: RadioCardWithTagsOption[] =
    modificationOptions?.map((modificationOption, i, options) => {
      // Add the price tag on large lot
      const cheaperOption = options.find(
        (o) =>
          o?.fields.price &&
          o?.fields.price < (modificationOption?.fields.price ?? 0),
      );
      // Selected have a white bg
      const isActiveBgColor =
        effectiveModificationOption?.fields.uniqueIdentifier !==
        modificationOption?.fields.uniqueIdentifier
          ? "bg-inherit"
          : "";
      return {
        value: modificationOption?.fields.internalValue,
        label: modificationOption?.fields.heading,
        description: modificationOption?.fields.subheading,
        tags:
          cheaperOption &&
          cheaperOption.fields.price &&
          modificationOption?.fields.price
            ? [
                `+ $${modificationOption?.fields.price - cheaperOption.fields.price}`,
              ]
            : [],
        className: `cursor-pointer border-system-grayscale-20 !min-h-[2.5rem] rounded-lg text-body border border-dashed sm:w-full md:w-auto flex-auto ${isActiveBgColor}`,
        badgeClassName: "whitespace-nowrap content-start",
      } as RadioCardWithTagsOption;
    }) ?? [];

  const {
    price: effectivePrice,
    promoPrice: effectivePromoPrice,
    internalValue: effectiveItem_variant_id,
    name: effectiveItem_name,
  } = effectiveModificationOption?.fields || {};

  // Fallback to priceToUse if effectivePrice is null/undefined
  const finalEffectivePrice = effectivePrice ?? priceToUse;

  const finalEffectivePromoPrice = effectivePromoPrice ?? promoPriceToUse;

  return (
    // One text color for the whole detail block — hierarchy comes from
    // size/weight; de-emphasis from /60 tints of the same hue.
    <div className="text-brand-primary-darker flex flex-1 flex-col justify-center gap-2 md:flex-row lg:flex-col">
      <div className="w-full bg-opacity-0 pb-3">
        <div className="flex flex-row md:flex-col">
          <div
            className={cn(
              "flex flex-col",
              variantResolved === "preview" && "px-4",
            )}
          >
            {variantResolved === "preview" && (icon1 || icon2) && (
              <div className="mr-3 flex shrink-0 flex-row items-start gap-2 md:mb-4 md:items-center">
                {icon1 && isIconName(icon1) && (
                  <div className="bg-brand-moss-greens-10 flex w-fit shrink-0 items-center rounded-full p-2">
                    <Icon
                      name={icon1}
                      className="text-brand-primary-regular h-[22px] w-[22px]"
                    />
                  </div>
                )}
                {icon2 && isIconName(icon2) && (
                  <div className="bg-brand-greens-light relative -left-4 -mr-3 flex w-fit shrink-0 items-center rounded-full p-2">
                    <Icon
                      name={icon2}
                      className="text-brand-primary-regular h-[24px] w-[24px]"
                    />
                  </div>
                )}
              </div>
            )}
            {primaryEyebrow && <Eyebrow text={primaryEyebrow} />}
            {isRecommended && (
              <B className="text-brand-primary-darker/70">
                Based on your answers we recommend:
              </B>
            )}
            <div
              className={pricePresentationVariants({
                headerContainer: pricePresentationVariant,
              })}
            >
              {headingToUse && variantResolved === "detail" && (
                <H1
                  className={layoutVariants({
                    headingText: variantResolved,
                  })}
                >
                  {headingToUse}
                </H1>
              )}
              {(headingToUse && variantResolved === "preview") ||
                (variantResolved === "modal" && (
                  <H2
                    className={cn(
                      layoutVariants({
                        headingText: variantResolved,
                      }),
                    )}
                  >
                    {headingToUse}
                  </H2>
                ))}
              {/* Pricing is here */}

              {finalEffectivePrice &&
                pricePresentationVariant === "Next to Header" && (
                  <div
                    className={pricePresentationVariants({
                      priceContainer: pricePresentationVariant,
                    })}
                  >
                    {hasPromoPrice(
                      finalEffectivePrice,
                      finalEffectivePromoPrice,
                    ) ? (
                      <div className="flex w-full basis-1 flex-row items-center">
                        <p className="text-brand-primary-darker/70">
                          <span className="pr-2 line-through">
                            ${finalEffectivePrice}
                          </span>
                        </p>
                        {showsPromoTotal(finalEffectivePromoPrice) && (
                          <div>${Number(finalEffectivePromoPrice)}</div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <b>${finalEffectivePrice}</b>
                      </div>
                    )}
                  </div>
                )}

              {finalEffectivePrice &&
                (pricePresentationVariant === "Below Header" ||
                  pricePresentationVariant === "Modal") && (
                  <div
                    className={pricePresentationVariants({
                      priceContainer: pricePresentationVariant,
                    })}
                  >
                    {isMinimalDetail ? (
                      showPriceWhileMinimal ? (
                        hasPromoPrice(
                          finalEffectivePrice,
                          finalEffectivePromoPrice,
                        ) ? (
                          <div className="flex w-full basis-1 flex-row items-center gap-4">
                            <div className="text-brand-primary-darker/70 line-through">
                              ${finalEffectivePrice + totalAddonPrice}
                            </div>
                            {showsPromoTotal(
                              finalEffectivePromoPrice + totalAddonPrice,
                            ) && (
                              <div>
                                $
                                {Number(
                                  finalEffectivePromoPrice + totalAddonPrice,
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>${finalEffectivePrice + totalAddonPrice}</div>
                        )
                      ) : (
                        <div className="hidden"></div>
                      )
                    ) : hasPromoPrice(
                        finalEffectivePrice,
                        finalEffectivePromoPrice,
                      ) ? (
                      <div className="flex w-full basis-1 flex-row items-center gap-4">
                        <div className="text-brand-primary-darker/70 line-through">
                          ${finalEffectivePrice + totalAddonPrice}
                        </div>
                        {showsPromoTotal(
                          finalEffectivePromoPrice + totalAddonPrice,
                        ) && (
                          <div>
                            $
                            {Number(finalEffectivePromoPrice + totalAddonPrice)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>${finalEffectivePrice + totalAddonPrice}</div>
                    )}

                    <div
                      className={cn(
                        "flex flex-row items-center",
                        isMinimalDetail && !showPriceWhileMinimal
                          ? "mx-0"
                          : "mx-4",
                      )}
                    >
                      <Link
                        href={REVIEWS_PAGE_HREF}
                        className="flex items-center justify-center gap-1.5"
                      >
                        <div className="flex h-full items-center">
                          {[...Array(5)].map((e, i) => (
                            <FontAwesomeIcon
                              key={`star-${i}`}
                              icon={faStar}
                              className="text-brand-primary-darker h-4 w-4 px-0.5"
                              aria-hidden="true"
                              role="presentation"
                              style={{
                                clipPath: `${i === 4 ? "inset(0 30% 0 0)" : ""}`,
                              }}
                            />
                          ))}
                        </div>
                        <P className="text-base" noSpacing>
                          {YARDZEN_REVIEW_COUNT} Reviews
                        </P>
                      </Link>
                    </div>
                  </div>
                )}
            </div>

            {installmentsToUse !== undefined &&
              installmentsToUse > 0 &&
              !isMinimalDetail && (
                <Badge
                  variant="white"
                  hover={false}
                  className="font-body mb-4 mt-2 w-fit gap-1 px-3 py-1 text-xs sm:text-sm"
                >
                  <span>
                    Pay {installmentsToUse} interest-free payments of{" "}
                    {formatter.format(installmentPriceToUse ?? 0)} with{" "}
                  </span>
                  {isIconName(affirmLogoIconName) && (
                    <Icon
                      name={affirmLogoIconName}
                      className="object-fit mb-1 h-auto w-[40px] pb-0.5"
                    />
                  )}
                </Badge>
              )}

            {displayDescription && description && (
              <div className="text-brand-primary-darker font-body mb-1 mt-2 text-left text-sm font-normal md:mb-0 md:mt-3 md:text-[15px] [&_p:last-child]:mb-2">
                {documentToReactComponents(description)}
              </div>
            )}
            {listItems && (
              <ListItemCollection
                listItemCollection={listItems}
                hideCollectionHeader
                listItemsContainerClassName={`mb-2 flex flex-row ${variantResolved !== "preview" ? "items-center [&:has(ul)]:items-start" : "items-center"} gap-2`}
                isModal={variantResolved === "modal"}
              />
            )}
            {modificationOptions &&
              !splitTreatment &&
              !isMinimalDetail &&
              variantResolved !== "modal" && (
                <Select
                  defaultValue={lotSize}
                  onChange={chooseModificationOption}
                  className="mb-6 mt-4 h-11 !w-full max-w-full rounded-md border-0 bg-white px-4 text-base font-normal shadow-lg shadow-black/5 focus:outline-none focus:ring-0 lg:mb-4 lg:mt-0"
                >
                  {modificationOptions.map((modificationOption) => (
                    <SelectItem
                      className="hover:bg-brand-beige-lighter h-12 bg-white px-4"
                      key={modificationOption?.fields.internalValue}
                      value={modificationOption?.fields.internalValue}
                    >
                      {modificationOption?.fields.heading}{" "}
                      {modificationOption?.fields.subheading}
                    </SelectItem>
                  ))}
                </Select>
              )}
            {addOnItems && splitTreatment && !isMinimalDetail && (
              <div>
                <div className="text-brand-primary-darker mb-2 mt-2 font-mono text-xs font-semibold uppercase tracking-wider">
                  Customize your plan
                </div>
                <div className="mb-4 flex flex-col gap-2 md:flex-row ">
                  {addOnItems.map(
                    (addonItem, i) =>
                      addonItem?.fields.uniqueIdentifier && (
                        <div
                          key={`PDI-addon-${addonItem?.sys.id}-${i}`}
                          className="flex w-fit min-w-0 max-w-full cursor-pointer items-center gap-2 rounded-lg bg-white p-3 transition-shadow duration-200 hover:shadow-md"
                          onClick={(e) => {
                            if (
                              e.target === e.currentTarget ||
                              !(e.target as Element).closest(
                                '[role="checkbox"]',
                              )
                            ) {
                              chooseAddonItem(
                                addonItem?.fields.stripeProductId ?? "",
                                addonItem?.fields.price ?? 0,
                              );
                            }
                          }}
                        >
                          <Checkbox
                            id={`PDI-addon-checkbox-${addonItem?.sys.id}-${i}`}
                            checked={currentAddonItems.some(
                              (item) =>
                                item.stripeProductId ===
                                addonItem.fields.stripeProductId,
                            )}
                            onCheckedChange={() =>
                              chooseAddonItem(
                                addonItem?.fields.stripeProductId ?? "",
                                addonItem?.fields.price ?? 0,
                              )
                            }
                            className="border-system-grayscale-50 h-4 w-4 rounded-sm"
                          />
                          <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1 leading-none">
                            <Label
                              htmlFor={`PDI-addon-checkbox-${addonItem?.sys.id}-${i}`}
                              className="whitespace-nowrap text-sm font-semibold"
                            >
                              {addonItem?.fields.heading}
                            </Label>
                            {addonItem?.fields.price && (
                              <Badge
                                variant="secondary"
                                hover={false}
                                className="whitespace-nowrap px-2 py-0.5 text-xs font-semibold"
                              >
                                + ${addonItem?.fields.price}
                              </Badge>
                            )}
                            <p className="text-brand-primary-darker/70 w-full min-w-0 text-start text-sm md:w-auto">
                              {addonItem?.fields.subheading}
                            </p>
                          </div>
                        </div>
                      ),
                  )}
                </div>
              </div>
            )}
            {modificationOptions && splitTreatment && !isMinimalDetail && (
              <div className="flex flex-col items-center gap-2 lg:flex-row">
                <div className="text-brand-primary-darker font-display mb-4 mt-2 text-base">
                  What's your lot size?
                </div>
                <RadioCardGroupWithTags
                  options={packageOptions}
                  defaultValue={initialModificationOption?.fields.internalValue}
                  onValueChange={chooseModificationOptionValue}
                  className="flex-row items-center"
                  labelClassName="text-sm font-semibold"
                />
              </div>
            )}

            {finalEffectivePrice &&
              pricePresentationVariant === "Above CTA" &&
              variantResolved === "preview" && (
                <div
                  className={pricePresentationVariants({
                    priceContainer: pricePresentationVariant,
                  })}
                >
                  <b>${finalEffectivePrice}</b>
                </div>
              )}
          </div>
        </div>

        {ctaToUse?.fields.buttonText &&
          !isMinimalDetail &&
          ctaUrl &&
          variantResolved !== "modal" && (
            <div
              id={addToCartId}
              className="mt-2 flex items-end p-6 px-0 pb-2 pt-0 md:mt-0 md:pt-2"
            >
              <Button
                size="lg"
                className="w-fit min-w-[260px] px-12"
                asChild
                onClick={async () => {
                  if (finalEffectivePrice) {
                    sendGTMEvent({
                      event: GtmEvents.ADD_TO_CART,
                      ...getGTMEventData(
                        finalEffectivePrice,
                        finalEffectivePromoPrice,
                        effectiveItem_variant_id ?? uniqueIdentifier,
                        effectiveItem_name ?? fullPackageName,
                      ),
                    });
                  }

                  // Clear the payment intent cookie before navigating to checkout
                  Cookies.remove("yz-payment-intent", { path: "/" });
                }}
              >
                <Link
                  className="hidden whitespace-nowrap font-medium max-[429px]:py-3 max-[390px]:px-3 max-[365px]:whitespace-normal max-[365px]:px-6 max-[365px]:leading-3 min-[360px]:inline-flex"
                  href={withBuilderPartnerParams(
                    hostName
                      ? ctaUrl.toString()
                      : ctaUrl.pathname + ctaUrl.search ||
                          "https://yardzen.com",
                  )}
                >
                  {ctaToUse?.fields.buttonText}
                </Link>
              </Button>
            </div>
          )}

        {isMinimalDetail &&
          showPriceWhileMinimal &&
          packageDetailItem?.fields.cta?.fields.url && (
            <Button className="mx-2 w-1/2" asChild>
              <Link
                href={withBuilderPartnerParams(
                  packageDetailItem?.fields.cta?.fields.url ?? "Add to cart",
                )}
              >
                {packageDetailItem?.fields.cta?.fields.buttonText}
              </Link>
            </Button>
          )}
      </div>
    </div>
  );
}
